import { type Fetcher } from '@atlassian/post-office-placement-data/types';
import { useFetchPlacementData } from '@atlassian/post-office-placement-data/useFetchPlacementData';
import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';
import { type SerializableRecord } from '@post-office/message-template-contracts/serializable-record';
import camelCase from 'lodash/camelCase.js';

import { type State } from './types';

export const createUsePlacementWithClient = (
	placementId: string,
	fetcher: Fetcher = { fetch: (...args) => globalThis.fetch(...args) },
) => {
	if (!fetcher.fetch) {
		throw new Error(
			'No fetcher found. If you are using usePlacement outside or a browser you must supply a fetcher yourself',
		);
	}

	return <TData extends SerializableRecord, TContext extends SerializableRecord>(
		path: string,
		context: TContext,
		requestOptions?: RequestInit,
		config?: {
			isEnabled?: boolean;
			// unused, will be removed in the future
			deps?: React.DependencyList;
			throttleTime?: number;
		},
	): State<TData> => {
		const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
		const analyticsSubjectId = placementId;
		const analyticsAttributes = {
			path: path,
		};

		const { data, error } = useFetchPlacementData<TData, TContext>(
			config?.isEnabled === false ? null : placementId,
			path,
			context,
			fetcher,
			requestOptions,
			{
				revalidateIfStale: false,
				revalidateOnFocus: false,
				revalidateOnReconnect: false,
				onSuccess: () => {
					fireAnalyticsEvent({
						action: 'resolved',
						actionSubject: 'dataPromise',
						actionSubjectId: camelCase(analyticsSubjectId) + 'DataPromise',
						eventType: 'operational',
						attributes: {
							...analyticsAttributes,
						},
					});
				},
				onError: (error: Error) => {
					fireAnalyticsEvent({
						action: 'rejected',
						actionSubject: 'dataPromise',
						actionSubjectId: camelCase(analyticsSubjectId) + 'DataPromise',
						eventType: 'operational',
						attributes: {
							...analyticsAttributes,
							errorName: error?.name ?? 'unknown',
							errorMessage: error?.message ?? 'unknown',
						},
					});
				},
			},
		);

		if (data) {
			return { isLoading: false, error: undefined, data };
		}
		if (error) {
			return { isLoading: false, error, data: undefined };
		}
		return { isLoading: true, error: undefined, data: undefined };
	};
};
